import type { TableSorting } from '@/modules/Workspaces/KnowledgeHub/Hubs/FileTable/sorting'
import type { HubFile } from '@/modules/Workspaces/KnowledgeHub/Hubs/types'
import { exhaustiveGuard } from '@/shared/utils/typeAssertions'

export type FolderNode = {
  id: string
  name: string
  children: TreeNode[]
  type: 'folder'
  path: string
}

export type FileNode = {
  id: string
  name: string
  children: TreeNode[]
  type: 'file'
  file: HubFile
}

export type TreeNode = FolderNode | FileNode

export type RowDescriptor =
  | ({ type: 'folder'; isExpanded: boolean } & FolderNode)
  | ({ type: 'file' } & FileNode)

/**
 * Builds a recursive folder tree out of a flat list of HubFiles.
 * Each intermediate folder that doesn't yet exist is created on the fly.
 */
export function buildFolderTree(files: HubFile[]): FolderNode {
  const root: FolderNode = {
    type: 'folder',
    id: 'folder:/',
    name: '',
    path: '',
    children: [],
  }
  const folderMap: Map<string, FolderNode> = new Map()
  folderMap.set(root.path, root)

  for (const file of files) {
    // Trim leading / and split
    const segments = file.fields.path.manualValue.replace(/^\//, '').split('/')
    const fileName = segments.pop() ?? ''

    let current = root
    let currentPath = ''

    for (const segment of segments) {
      currentPath += '/' + segment
      let folder = folderMap.get(currentPath)

      if (!folder) {
        folder = {
          type: 'folder',
          id: `folder:${currentPath}`,
          name: segment,
          path: currentPath,
          children: [],
        }
        current.children.push(folder)
        folderMap.set(currentPath, folder)
      }
      current = folder
    }

    // Push file node
    const name = fileName || file.fileName
    current.children.push({
      type: 'file',
      id: `file:${file.entity.id}`,
      name,
      file: { ...file, fileName: name },
      children: [],
    })
  }

  return root
}

/** Sorts folders A→Z, then files according to a user-selected column/direction */
export function sortTree(node: FolderNode, sort: TableSorting) {
  const [sortColumn, direction] = sort

  node.children.sort((a, b) => {
    // Folders always first
    if (a.type === 'folder' && b.type === 'file') return -1
    if (a.type === 'file' && b.type === 'folder') return 1

    // Both folders – alphabetical by name
    if (a.type === 'folder' && b.type === 'folder') {
      return a.name.localeCompare(b.name)
    }

    // Both files – apply user sort
    if (a.type === 'file' && b.type === 'file') {
      const left = direction === 'desc' ? b.file : a.file
      const right = direction === 'desc' ? a.file : b.file

      switch (sortColumn) {
        case 'filename':
          return left.fileName.localeCompare(right.fileName)
        case 'createdAt':
          return left.fields.file.createdAt.localeCompare(right.fields.file.createdAt)
        default:
          return exhaustiveGuard(sortColumn, 'Missing sort column')
      }
    }

    return 0
  })

  for (const child of node.children) {
    if (child.type === 'folder') sortTree(child, sort)
  }
}

/** Utility that returns all ancestor folder paths of given absolute file path */
export function ancestorsOf(path: string): string[] {
  const segments = path.replace(/^\//, '').split('/')
  const ancestors: string[] = []
  let current = ''
  for (const segment of segments.slice(0, -1)) {
    current += '/' + segment
    ancestors.push(current)
  }
  return ancestors
}
